//////////////////
// IMPORTS
//////////////////
// libraries
import Radium from "radium";
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// suggestion-action imports
import { IContainer, IProps, TTrackingData } from "./SuggestionAction.d";
import { suggestionActionFragments } from "./SuggestionAction.gql";
import * as s from "./SuggestionAction.styles";
// other actions
import { SuggestionActionAutopilot } from "./suggestion-action-autopilot";
import { SuggestionActionConfigSettings } from "./suggestion-action-config-settings";
import { SuggestionActionDecline } from "./suggestion-action-decline";
import { SuggestionActionFyi } from "./suggestion-action-fyi";
import { SuggestionActionInvite } from "./suggestion-action-invite";
import { SuggestionActionRemoveAttendee } from "./suggestion-action-remove-attendee";
import { SuggestionActionRequestToMove } from "./suggestion-action-request-to-move";
import { SuggestionActionReschedule } from "./suggestion-action-reschedule";
import { SuggestionActionViewMeeting } from "./suggestion-action-view-meeting";
import { SuggestionActionViewProfile } from "./suggestion-action-view-profile";
import { SuggestionActionViewTeamDashboard } from "./suggestion-action-view-team-dashboard";
// state
import { IReduxState } from "@reducers/root.reducer";
// util
import { EActionPayloadType, maybeGetRichSuggestionConfig } from "@util/suggestion.util";
import { EFlex } from "@util/event-tag.util";

//////////////////
// COMPONENT
//////////////////
@Radium
class SuggestionActionCmpt extends React.Component<IProps> {
  // ~-~-~-~-~-~-~-~-~-~-
  // Callback Handling
  // ~-~-~-~-~-~-~-~-~-~-
  private markSeen = (trackingData: TTrackingData, isSynchronous = false, isDismiss = false) => {
    const { feedSuggestion, actionCallback } = this.props;
    const markSeenFeedSuggestionInput = {
      feedSuggestionRelayId: feedSuggestion.id,
      primaryActionTaken: isDismiss ? undefined : feedSuggestion.primaryAction.type,
    };
    actionCallback({ markSeenFeedSuggestionInput, isSynchronous }, trackingData);
  };

  private markSeenDefault = (trackingData?: TTrackingData) => {
    this.markSeen(trackingData || {}, false);
  };

  private markSeenSynchronous = (trackingData?: TTrackingData) => {
    this.markSeen(trackingData || {}, true);
  };

  private markSeenAutopilot = (trackingData?: TTrackingData) => {
    // if autoPilot option is to pin, we want to record this as though it's a dismiss
    const isDismiss = trackingData && trackingData.flex === EFlex.Pin;
    this.markSeen(trackingData || {}, false, isDismiss);
  };

  // ~-~-~-~-~-~-~-~-~-~-
  // Rendering
  // ~-~-~-~-~-~-~-~-~-~-
  public render() {
    const { feedSuggestion, org, autopilotOptionsUpdate } = this.props;
    const { payload, display } = feedSuggestion.primaryAction;

    // tip and config actions have color backgrounds
    const richCardConfig = maybeGetRichSuggestionConfig(feedSuggestion.suggestionPayload, org);
    const backgroundColor = (!!richCardConfig && richCardConfig.actionBackground) || undefined;
    const buttonTextColor = (!!richCardConfig && richCardConfig.colorText) || undefined;
    const containerStyles = !!backgroundColor
      ? { ...s.container, backgroundColor, borderTop: "none" }
      : s.container;

    // most of these have similar props
    const sharedProps = {
      backgroundColor,
      buttonTextColor,
      label: display,
    };

    switch (payload.__typename) {
      case EActionPayloadType.Autopilot:
        return (
          <SuggestionActionAutopilot
            {...sharedProps}
            payload={payload}
            actionCallback={this.markSeenAutopilot}
            autopilotOptionsUpdate={autopilotOptionsUpdate}
            org={org}
          />
        );
      case EActionPayloadType.ConfigureSettings:
        return (
          <div style={containerStyles}>
            <SuggestionActionConfigSettings
              {...sharedProps}
              payload={payload}
              actionCallback={this.markSeenDefault}
              org={org}
            />
          </div>
        );
      case EActionPayloadType.Decline:
        return (
          <div style={containerStyles}>
            <SuggestionActionDecline
              {...sharedProps}
              payload={payload}
              actionCallback={this.markSeenDefault}
            />
          </div>
        );
      case EActionPayloadType.Fyi:
        return (
          <div style={containerStyles}>
            <SuggestionActionFyi
              {...sharedProps}
              payload={payload}
              actionCallback={this.markSeenDefault}
            />
          </div>
        );
      case EActionPayloadType.Invite:
        return (
          <div style={containerStyles}>
            <SuggestionActionInvite
              {...sharedProps}
              payload={payload}
              actionCallback={this.markSeenDefault}
              org={org}
            />
          </div>
        );
      case EActionPayloadType.RemoveAttendee:
        return (
          <div style={containerStyles}>
            <SuggestionActionRemoveAttendee
              {...sharedProps}
              payload={payload}
              actionCallback={this.markSeenDefault}
            />
          </div>
        );
      case EActionPayloadType.RequestToMove:
        return (
          <div style={containerStyles}>
            <SuggestionActionRequestToMove
              {...sharedProps}
              payload={payload}
              actionCallback={this.markSeenDefault}
            />
          </div>
        );
      case EActionPayloadType.Reschedule:
        return (
          <div style={containerStyles}>
            <SuggestionActionReschedule
              {...sharedProps}
              payload={payload}
              actionCallback={this.markSeenSynchronous}
            />
          </div>
        );
      case EActionPayloadType.ViewMeeting:
        return (
          <div style={containerStyles}>
            <SuggestionActionViewMeeting
              {...sharedProps}
              payload={payload}
              actionCallback={this.markSeenSynchronous}
            />
          </div>
        );
      case EActionPayloadType.ViewProfile:
        return (
          <div style={containerStyles}>
            <SuggestionActionViewProfile
              {...sharedProps}
              payload={payload}
              actionCallback={this.markSeenSynchronous}
            />
          </div>
        );
      case EActionPayloadType.ViewTeamDashboard:
        return (
          <div style={containerStyles}>
            <SuggestionActionViewTeamDashboard
              {...sharedProps}
              payload={payload}
              actionCallback={this.markSeenSynchronous}
            />
          </div>
        );
      default:
        return "FOOTER!";
    }
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const SuggestionActionRedux = connect(mapStateToProps, {})(SuggestionActionCmpt);

//////////////////
// RELAY
//////////////////
export const SuggestionAction = createFragmentContainer<IContainer>(
  SuggestionActionRedux,
  suggestionActionFragments,
);
